// src/pwa/install.ts: the install prompt's rules, every one pure (docs/DECISIONS.md, "Install prompt"; the PWA plan,
// sections 4.1 to 4.3). src/pwa/prompt.ts runs them against the live page; the owner's design draws the card.
//   platform     android-prompt: Chrome (or another Chromium) handed over its install event, so one tap opens the
//                native dialog. ios-safari: Share, then Add to Home Screen (behind "•••" on iOS 26 and later), with
//                "Open as Web App" left on. ios-other-browser: Chrome and Edge on iOS, which add to the Home Screen
//                from their own Share menu. in-app-browser: Gmail's, Facebook's, Instagram's and the like, which cannot
//                install at all ("Open in Safari" or "Open in Chrome" first). desktop. unsupported: any other phone
//                browser, and Android until Chrome's event arrives (it never comes once the app is installed).
//   the card     a signed-in household member, on a phone or tablet, in the browser (not the app), on a board screen
//                (never /start, /signin, /auth/*, Sort or Unsorted), after the first day this device saw them. Start
//                Your Build's Sent page is the exception: that trigger only ever happens on the first day. Never over
//                the board's welcome sheet.
//   triggers     message (just sent to the team), start_sent (right after Start Your Build's Sent page),
//                second_visit (the first visit on a later day). The first one wins (a second visit gives way to the
//                other two); unanswered, it lapses.
//   frequency    once per device per 14 days, at most 3 times; Not now snoozes 14 days; Don't ask again ends it, and
//                so does an install (or a launch of the app, where it shares storage with the browser: Android).
// User-agent sniffing is best effort by nature: Gmail on Android opens links in a Custom Tab, which looks like Chrome
// (and never gets the install event, so it lands on unsupported), and Safari's in-app view looks like Safari.
import type { RouteName } from "../app/router";
import { pwaAllows, type PwaMode } from "../../shared/pwa";

export type InstallPlatform = "android-prompt" | "ios-safari" | "ios-other-browser" | "in-app-browser" | "desktop" | "unsupported";
export type InstallOs = "ios" | "android" | "other";
/** demo_tour: the demo tour's app stop asked for the card (src/demo/tour.ts). */
export type InstallTrigger = "message" | "start_sent" | "second_visit" | "demo_tour";
/** Where an install began: the card, the Settings row, the /install page, or the browser's own menu. */
export type InstallSource = "card" | "settings" | "page" | "browser";
/** How the card was put away: Not now (or closed), Don't ask again, or Cancel in Chrome's own dialog. */
export type InstallChoice = "not_now" | "never" | "native_cancel";
/**
 * What the main action did. installed / dismissed: Chrome's dialog and the family's answer (Android). guide: nothing
 * can open for them; show the steps (iOS). open-in-browser: an in-app browser; show "Open in Safari/Chrome first".
 * unavailable: nothing to offer (PWA_MODE off, the app, a desktop, an unsupported browser, or the event was used).
 */
export type InstallOutcome = "installed" | "dismissed" | "guide" | "open-in-browser" | "unavailable";
export type InstallViewer = "household" | "staff" | "signed-out";

/** Chrome's `beforeinstallprompt` event (not in the DOM types): prompt() works once per event. */
export type InstallPromptEvent = Event & {
  prompt(): Promise<unknown>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform?: string }>;
};

export const DAY_MS = 864e5;
/** Once per device per this long. */
export const SHOW_EVERY_MS = 14 * DAY_MS;
/** At most this many cards per device, ever. */
export const MAX_SHOWS = 3;
/** "Not now" (or closing the card, or Cancel in Chrome's dialog) waits this long. */
export const SNOOZE_MS = 14 * DAY_MS;
/** A trigger that no card showed within this long lapses (a card never appears long after its moment). */
export const TRIGGER_TTL_MS = 2 * 60_000;
/**
 * Triggers the first-day rule lets through: Start Your Build's Sent page is, by definition, the first day, and the
 * demo tour's app stop is asked for.
 */
export const FIRST_DAY_TRIGGERS: readonly InstallTrigger[] = ["start_sent", "demo_tour"];
/**
 * The screens a card may appear on: the family's boards (and the board picker). Everything else is out, including
 * the plan's exclusions (/start and its sent pages, /signin, /auth/*, Sort, and Unsorted, which is Sort's task screen
 * under another address) and pages that are no place for it (/install itself, /unsubscribe, /privacy, the demo's and
 * the team's pages).
 */
export const CARD_ROUTES: readonly RouteName[] = ["root", "home", "rooms", "room", "item", "messages", "people", "settings"];
/** The platforms that have something to offer: the native dialog, the steps, or "open the real browser first". */
export const OFFERING_PLATFORMS: readonly InstallPlatform[] = ["android-prompt", "ios-safari", "ios-other-browser", "in-app-browser"];

// ------------------------------------------------------------------ the browser

/**
 * In-app browsers by the marks their apps add: Facebook (FBAN/FBAV/FB_IAB/FBIOS), Instagram, LinkedIn, the Google
 * app (GSA, where Gmail on iOS may send links), LINE, Snapchat, WeChat, Pinterest, TikTok and X.
 */
const IN_APP_MARK = /\b(?:FBAN|FBAV|FB_IAB|FBIOS|FB4A)\b|\bInstagram\b|\bLinkedInApp\b|\bGSA\/|\bLine\/|\bSnapchat\b|\bMicroMessenger\/|\bPinterest\b|\bmusical_ly\b|\bBytedanceWebview\b|\bTwitter\b/i;
/** Android's WebView (any app that shows pages itself) says "; wv)". */
const ANDROID_WEBVIEW = /;\s*wv\)/;
/** The iOS browsers the plan confirmed can add to the Home Screen from their own Share menu (iOS 16.4 and later). */
const IOS_OTHER_BROWSER = /\b(?:CriOS|EdgiOS)\//;
/** Other iOS browsers: whether their menus can add to the Home Screen is unverified, so they are unsupported. */
const IOS_UNVERIFIED_BROWSER = /\b(?:FxiOS|OPiOS|OPT|DuckDuckGo|YaBrowser)\//;
const OTHER_MOBILE = /Mobi|Tablet|KAIOS|Windows Phone|BlackBerry|BB10|Opera Mini|Silk\/|Kindle/i;

export type BrowserInfo = {
  /** navigator.userAgent. */
  ua: string;
  /**
   * navigator.maxTouchPoints: iPadOS asks for desktop sites as a Mac, and Chrome on a large Android tablet asks for
   * them as Linux by default; only the touch screen gives either away.
   */
  touchPoints?: number;
  /** Chrome's install event is in hand (and not used yet). */
  canPrompt?: boolean;
};

/**
 * The phone's system: iOS includes iPadOS (a "Macintosh" with a touch screen), and Android includes a tablet asking
 * for desktop sites ("X11; Linux" with a touch screen; a Linux computer with one is taken for a tablet, and a
 * Chromebook says "CrOS").
 */
export function detectOs(b: BrowserInfo): InstallOs {
  const touch = (b.touchPoints ?? 0) > 1;
  if (/\b(?:iPhone|iPad|iPod)\b/.test(b.ua)) return "ios";
  if (/\bMacintosh\b/.test(b.ua) && touch) return "ios";
  if (/\bAndroid\b/.test(b.ua)) return "android";
  if (/\bX11; Linux\b/.test(b.ua) && touch) return "android";
  return "other";
}

/** An app's own browser (best effort, by user agent). On iOS any WebKit view without Safari's mark counts. */
export function isInAppBrowser(b: BrowserInfo): boolean {
  if (IN_APP_MARK.test(b.ua)) return true;
  const os = detectOs(b);
  if (os === "android") return ANDROID_WEBVIEW.test(b.ua);
  // Safari, Chrome, Edge and Firefox on iOS all carry "Safari/"; an app's WKWebView does not. (So does the installed
  // app itself: this answers for the browser, and callers check standalone first.)
  if (os === "ios") return !/\bSafari\//.test(b.ua);
  return false;
}

/** Which of the six install situations this browser is in (the browser, not the installed app). */
export function detectPlatform(b: BrowserInfo): InstallPlatform {
  const os = detectOs(b);
  if (os === "other") return OTHER_MOBILE.test(b.ua) ? "unsupported" : "desktop";
  if (isInAppBrowser(b)) return "in-app-browser";
  if (os === "ios") {
    if (IOS_OTHER_BROWSER.test(b.ua)) return "ios-other-browser";
    if (IOS_UNVERIFIED_BROWSER.test(b.ua)) return "unsupported";
    return "ios-safari";
  }
  return b.canPrompt ? "android-prompt" : "unsupported";
}

/**
 * How the iPhone steps read (the install sheet, src/pwa/InstallCard.tsx). safari-26: Safari 26 and later, whose
 * compact bar keeps Share behind "•••" and whose Add to Home Screen asks "Open as Web App". safari: earlier Safari,
 * with Share on the toolbar. other-browser: Chrome and Edge on iOS, each with its own Share button. iOS 26 froze the
 * system version in the user agent ("OS 18_6"), so Safari's own ("Version/26.0") tells them apart. Null off iOS.
 */
export type IosGuide = "safari-26" | "safari" | "other-browser";
export function iosGuideOf(platform: InstallPlatform, ua: string): IosGuide | null {
  if (platform === "ios-other-browser") return "other-browser";
  if (platform !== "ios-safari") return null;
  const v = /\bVersion\/(\d+)/.exec(ua);
  return v && Number(v[1]) >= 26 ? "safari-26" : "safari";
}

/** Phones and tablets: the card never appears on a computer. */
export function isPhoneOrTablet(b: BrowserInfo): boolean {
  return detectOs(b) !== "other";
}

/**
 * From an in-app browser on Android, a link that opens `url` in Chrome (an intent URL; best effort: most in-app
 * browsers follow it, a few show an error). Null anywhere else: iOS has no such link.
 */
export function chromeIntentHref(url: string, b: BrowserInfo): string | null {
  if (detectOs(b) !== "android" || !isInAppBrowser(b)) return null;
  try {
    const u = new URL(url);
    if (u.protocol !== "https:" && u.protocol !== "http:") return null;
    const scheme = u.protocol.slice(0, -1);
    return `intent://${u.host}${u.pathname}${u.search}#Intent;scheme=${scheme};package=com.android.chrome;S.browser_fallback_url=${encodeURIComponent(u.href)};end`;
  } catch {
    return null;
  }
}

// ------------------------------------------------------------------ who is looking, and where

/** A household member is signed in and not staff (staff get the Settings row, never the card). */
export function viewerOf(me: { user: unknown; staff: unknown } | null | undefined): InstallViewer {
  if (!me?.user) return "signed-out";
  return me.staff ? "staff" : "household";
}

export function cardRoute(name: RouteName): boolean {
  return CARD_ROUTES.includes(name);
}

/** The local calendar day of an instant ("2026-09-23"): "first day" and "a later day" are the family's days. */
export function localDay(ms: number): string {
  const d = new Date(ms);
  const two = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${two(d.getMonth() + 1)}-${two(d.getDate())}`;
}

// ------------------------------------------------------------------ this device's memory (src/lib/storage.ts)

export type InstallMemory = {
  /** The first day a household member used this browser, and the last. */
  firstDay: string | null;
  lastVisitDay: string | null;
  /** Cards shown, and when the last one was. */
  shows: number;
  lastShownAt: number | null;
  /** Not now. */
  snoozedUntil: number | null;
  /** Don't ask again. */
  never: boolean;
  /** Installed from here, or the app launched with this browser's storage. */
  installed: boolean;
};

export const EMPTY_MEMORY: InstallMemory = { firstDay: null, lastVisitDay: null, shows: 0, lastShownAt: null, snoozedUntil: null, never: false, installed: false };

const DAY = /^\d{4}-\d{2}-\d{2}$/;

/** Stored memory, field by field: anything missing or malformed takes its empty value (never a crash, never a card). */
export function parseMemory(raw: unknown): InstallMemory {
  if (!raw || typeof raw !== "object") return { ...EMPTY_MEMORY };
  const r = raw as Record<string, unknown>;
  const day = (v: unknown) => (typeof v === "string" && DAY.test(v) ? v : null);
  const time = (v: unknown) => (typeof v === "number" && Number.isFinite(v) && v > 0 ? v : null);
  return {
    firstDay: day(r.firstDay),
    lastVisitDay: day(r.lastVisitDay),
    shows: typeof r.shows === "number" && Number.isInteger(r.shows) && r.shows > 0 ? Math.min(r.shows, MAX_SHOWS) : 0,
    lastShownAt: time(r.lastShownAt),
    snoozedUntil: time(r.snoozedUntil),
    never: r.never === true,
    installed: r.installed === true,
  };
}

/**
 * A household member's visit on `day`. The first ever sets the first day; the first visit on each later day is a
 * second visit (its trigger is raised once that day, not on every page load).
 */
export function noteVisit(m: InstallMemory, day: string): { memory: InstallMemory; secondVisit: boolean } {
  if (!m.firstDay) return { memory: { ...m, firstDay: day, lastVisitDay: day }, secondVisit: false };
  const secondVisit = day !== m.firstDay && day !== m.lastVisitDay;
  return { memory: m.lastVisitDay === day ? m : { ...m, lastVisitDay: day }, secondVisit };
}

/**
 * The frequency rules for a trigger: the demo tour's app stop was asked for, so only Don't ask again and an install
 * hold it back (not the interval, the snooze or the 3-card cap).
 */
export function mayShowFor(trigger: InstallTrigger, m: InstallMemory, now: number): boolean {
  return trigger === "demo_tour" ? !m.never && !m.installed : mayShowAgain(m, now);
}

/**
 * The frequency rules alone: whether this device may see another card at `now` (once per 14 days, at most 3 times,
 * never after Don't ask again or an install, and not while snoozed).
 */
export function mayShowAgain(m: InstallMemory, now: number): boolean {
  if (m.never || m.installed || m.shows >= MAX_SHOWS) return false;
  if (m.lastShownAt !== null && now - m.lastShownAt < SHOW_EVERY_MS) return false;
  return m.snoozedUntil === null || now >= m.snoozedUntil;
}

export function afterShown(m: InstallMemory, now: number): InstallMemory {
  return { ...m, shows: Math.min(m.shows + 1, MAX_SHOWS), lastShownAt: now };
}
export function afterNotNow(m: InstallMemory, now: number): InstallMemory {
  return { ...m, snoozedUntil: now + SNOOZE_MS };
}
export function afterNever(m: InstallMemory): InstallMemory {
  return { ...m, never: true };
}
export function afterInstalled(m: InstallMemory): InstallMemory {
  return { ...m, installed: true };
}

// ------------------------------------------------------------------ triggers and the card

export type PendingTrigger = { trigger: InstallTrigger; at: number };

/** Whether a raised trigger still stands at `now` (it lapses after TRIGGER_TTL_MS). */
export function triggerStands(p: PendingTrigger | null, now: number): p is PendingTrigger {
  return !!p && now - p.at < TRIGGER_TTL_MS;
}

/**
 * The first trigger wins: a new one replaces the pending one only once that has lapsed. The exception is a second
 * visit, which gives way to a moment the family just made (a message, Start Your Build): it is raised as the page
 * loads or as someone signs in partway through it, which on a phone that saw another household member on an earlier
 * day is the sent page's code or Start Your Build's own new session, just before start_sent.
 */
export function pickTrigger(pending: PendingTrigger | null, incoming: InstallTrigger, now: number): PendingTrigger {
  if (incoming === "demo_tour" || !triggerStands(pending, now)) return { trigger: incoming, at: now };
  return pending.trigger === "second_visit" && incoming !== "second_visit" ? { trigger: incoming, at: now } : pending;
}

export type CardContext = {
  mode: PwaMode;
  /** The tenant has an app (brand.app); without it there is no manifest to install. */
  hasApp: boolean;
  viewer: InstallViewer;
  platform: InstallPlatform;
  /** A phone or tablet (isPhoneOrTablet). */
  mobile: boolean;
  standalone: boolean;
  route: RouteName;
  today: string;
  firstDay: string | null;
  trigger: InstallTrigger;
  /** The board's welcome sheet, a modal, is on its way or up (src/start/sheet-flag.ts): the card waits for it. */
  welcomeSheet: boolean;
};
export type CardBlocker = "off" | "no_app" | "viewer" | "device" | "standalone" | "platform" | "route" | "welcome_sheet" | "first_day";

/** Why the card may not show for this trigger here and now, or null when it may (the frequency rules are separate). */
export function cardBlocker(c: CardContext): CardBlocker | null {
  if (!pwaAllows(c.mode, "install")) return "off";
  if (!c.hasApp) return "no_app";
  if (c.viewer !== "household") return "viewer";
  if (!c.mobile) return "device";
  if (c.standalone) return "standalone";
  if (!OFFERING_PLATFORMS.includes(c.platform)) return "platform";
  if (!cardRoute(c.route)) return "route";
  if (c.welcomeSheet) return "welcome_sheet";
  const firstDay = !c.firstDay || c.firstDay === c.today;
  if (firstDay && !FIRST_DAY_TRIGGERS.includes(c.trigger)) return "first_day";
  return null;
}

/**
 * Whether the Settings row and /install offer the app: installing is on, in the browser, and there is something to
 * offer here. Anyone may see it (staff too); it is never pushed.
 */
export function offersInstall(c: Pick<CardContext, "mode" | "hasApp" | "platform" | "standalone">): boolean {
  return pwaAllows(c.mode, "install") && c.hasApp && !c.standalone && OFFERING_PLATFORMS.includes(c.platform);
}

/** What the main action does on each platform: Chrome's dialog, the steps, "open the real browser first", or nothing. */
export type InstallAction = "prompt" | "guide" | "open-in-browser" | "unavailable";

export function actionFor(platform: InstallPlatform): InstallAction {
  if (platform === "android-prompt") return "prompt";
  if (platform === "ios-safari" || platform === "ios-other-browser") return "guide";
  if (platform === "in-app-browser") return "open-in-browser";
  return "unavailable";
}

// ------------------------------------------------------------------ launches

export type Launch = { standalone: boolean; fromIcon: boolean };

/** How this page was opened: as the installed app, and from the home-screen icon (the manifest's start_url, ?src=app). */
export function launchOf(search: string, standalone: boolean): Launch {
  let fromIcon = false;
  try {
    fromIcon = new URLSearchParams(search).get("src") === "app";
  } catch {
    fromIcon = false;
  }
  return { standalone, fromIcon };
}

/** A launch worth recording: the app, or its icon (iOS 26 and later can add a plain bookmark, "Open as Web App" off). */
export function isLaunch(l: Launch): boolean {
  return l.standalone || l.fromIcon;
}
